// The Shuttle Landing Facility: the runway spaceplanes take off from.
//
// Placed on the real one, north-west of LC-39 and west of the Banana Creek
// marsh. The centreline was fitted to the runway in the Cape's own ground
// imagery (ksc_far.webp), so the pavement drawn here lies on the photo's:
// 28.61439 N, 80.69401 W, 5.13 km end to end with the overruns, true
// heading 149.6 degrees from the north-west end. The paved runway is 4572 m
// by 91 m with a 305 m overrun at each end.
//
// Frames: `along` runs from the north-west threshold toward the south-east
// (metres from the centre), `across` to the right of that direction. Physics
// reads runwayHeightAt() for the pavement; site.js builds the mesh through
// runwayToSite(); the launch flow stands a plane at the threshold with
// runwayStart().

import * as THREE from 'three';

const DEG = Math.PI / 180;
const EARTH_R = 6371000;

export const RUNWAY = {
  lat: 28.614385,
  lon: -80.694012,
  heading: 149.6,          // true heading from the north-west end
  length: 4572,
  width: 91,
  overrun: 305,
  shoulder: 23,            // paved shoulders each side
  // Ground under it: levelled at the site's own height (terrain.js FLATS),
  // pavement a hand's breadth proud of it.
  ground: 3,
  pavement: 0.12,
  // The Spaceplane Hangar and its apron, on the real SLF apron east of the
  // south-east end: `along` and `across` in metres (negative across is the
  // runway's left, the east side).
  hangar: { along: 1750, across: -330 }
};

// Surface-fixed unit vectors at a latitude and longitude (bodies.js frame).
function basisAt(latDeg, lonDeg) {
  const lat = latDeg * DEG, lon = lonDeg * DEG;
  const cl = Math.cos(lat);
  const up = { x: cl * Math.cos(lon), y: Math.sin(lat), z: -cl * Math.sin(lon) };
  let east = { x: up.z, y: 0, z: -up.x };            // (0,1,0) x up
  const el = Math.hypot(east.x, east.z) || 1;
  east = { x: east.x / el, y: 0, z: east.z / el };
  const north = { x: up.y * east.z - up.z * east.y, y: up.z * east.x - up.x * east.z, z: up.x * east.y - up.y * east.x };
  return { up, east, north };
}

const B = basisAt(RUNWAY.lat, RUNWAY.lon);
const H = RUNWAY.heading * DEG;
// Along the runway and to its right, in surface-fixed axes.
const DIR = { x: B.east.x * Math.sin(H) + B.north.x * Math.cos(H), y: B.east.y * Math.sin(H) + B.north.y * Math.cos(H), z: B.east.z * Math.sin(H) + B.north.z * Math.cos(H) };
const RIGHT = { x: DIR.y * B.up.z - DIR.z * B.up.y, y: DIR.z * B.up.x - DIR.x * B.up.z, z: DIR.x * B.up.y - DIR.y * B.up.x };
const HALF_PAVED = RUNWAY.length / 2 + RUNWAY.overrun;

// Runway coordinates of a surface direction (unit or not), metres.
export function runwayCoords(p) {
  const r = Math.hypot(p.x, p.y, p.z);
  const ux = p.x / r, uy = p.y / r, uz = p.z / r;
  // Tangent-plane projection is exact to a millimetre over 3 km.
  return {
    along: (ux * DIR.x + uy * DIR.y + uz * DIR.z) * EARTH_R,
    across: (ux * RIGHT.x + uy * RIGHT.y + uz * RIGHT.z) * EARTH_R,
    up: ux * B.up.x + uy * B.up.y + uz * B.up.z
  };
}

// Height (m above the sphere) of the pavement at a latitude and longitude in
// radians, or null off it. Earth only.
export function runwayHeightAt(bodyKey, lat, lon) {
  if (bodyKey !== 'earth') return null;
  // Cheap reject: the runway sits inside 0.03 degrees of its centre.
  if (Math.abs(lat / DEG - RUNWAY.lat) > 0.03 || Math.abs(lon / DEG - RUNWAY.lon) > 0.035) return null;
  const cl = Math.cos(lat);
  const c = runwayCoords({ x: cl * Math.cos(lon), y: Math.sin(lat), z: -cl * Math.sin(lon) });
  if (Math.abs(c.along) > HALF_PAVED || Math.abs(c.across) > RUNWAY.width / 2 + RUNWAY.shoulder) return null;
  return RUNWAY.ground + RUNWAY.pavement;
}

// Surface-fixed position (metres) of runway coordinates at a height above the sphere.
export function runwaySurfacePoint(along, across, height = RUNWAY.ground) {
  const R = EARTH_R + height;
  const x = B.up.x + (DIR.x * along + RIGHT.x * across) / EARTH_R;
  const y = B.up.y + (DIR.y * along + RIGHT.y * across) / EARTH_R;
  const z = B.up.z + (DIR.z * along + RIGHT.z * across) / EARTH_R;
  const l = Math.hypot(x, y, z);
  return { x: x / l * R, y: y / l * R, z: z / l * R };
}

// Site-frame (x east, z south, metres from `site`'s centre) position and the
// runway's direction in that frame, for building the mesh in site.js.
export function runwayToSite(site) {
  const sb = basisAt(site.lat, site.lon);
  const toSite = (along, across) => {
    const p = runwaySurfacePoint(along, across, 0);
    const d = { x: p.x / EARTH_R - sb.up.x, y: p.y / EARTH_R - sb.up.y, z: p.z / EARTH_R - sb.up.z };
    return {
      x: (d.x * sb.east.x + d.y * sb.east.y + d.z * sb.east.z) * EARTH_R,
      z: -(d.x * sb.north.x + d.y * sb.north.y + d.z * sb.north.z) * EARTH_R
    };
  };
  const c = toSite(0, 0), a = toSite(1000, 0);
  const dx = (a.x - c.x) / 1000, dz = (a.z - c.z) / 1000;
  return { centre: c, dir: new THREE.Vector2(dx, dz).normalize(), toSite };
}

// Where a plane lines up: on the centreline 150 m in from the north-west
// threshold, pointing down the runway.
export function runwayStart() {
  const along = -RUNWAY.length / 2 + 150;
  return { along, across: 0, heading: RUNWAY.heading, surface: runwaySurfacePoint(along, 0, RUNWAY.ground + RUNWAY.pavement), up: B.up, east: B.east, north: B.north };
}

// The ground levelled for the runway, the taxiway and the hangar apron:
// overlapping discs along the centreline for terrain.js's FLATS.
export function runwayFlats() {
  const out = [];
  const n = 9;
  for (let i = 0; i < n; i++) {
    const along = -HALF_PAVED + (i + 0.5) * (2 * HALF_PAVED / n);
    const p = runwaySurfacePoint(along, 0, 0);
    const r = Math.hypot(p.x, p.y, p.z);
    out.push({ lat: Math.asin(p.y / r) / DEG, lon: Math.atan2(-p.z, p.x) / DEG, radius: 380, height: RUNWAY.ground, fade: 260 });
  }
  const hp = runwaySurfacePoint(RUNWAY.hangar.along, RUNWAY.hangar.across, 0);
  const hr = Math.hypot(hp.x, hp.y, hp.z);
  out.push({ lat: Math.asin(hp.y / hr) / DEG, lon: Math.atan2(-hp.z, hp.x) / DEG, radius: 320, height: RUNWAY.ground, fade: 200 });
  return out;
}
